import { createFileRoute, linkOptions, Outlet } from '@tanstack/react-router'
import type { TableMeta } from '@tanstack/react-table'
import { useAtomValue } from 'jotai'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type CoverageByCriteriaRequest,
  type CoverageFilters,
  type CoverageResponse,
  CoverageSortingCriteria,
  type CoverageSortingState,
  CoverageStatus,
  type DashboardKpiRequest,
  type InsistenceFilters,
  type InsistenceSortingState,
  InsistenceStatus,
} from 'types'

import PortfolioSelect from '@/components/portfolio-select'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import { InsistencesColumn } from '@/components/tables/insistences/insistences-columns.enum'
import InsistencesTable from '@/components/tables/insistences/insistences-table'
import { DEFAULT_PAGE_SIZE } from '@/lib/table'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'

import CoverageByCriteria from '@/components/coverage/coverage-by-criteria/coverage-by-criteria'
import { CoveragesColumn } from '@/components/tables/coverages/coverages-columns.enum'
import CoveragesTable from '@/components/tables/coverages/coverages-table'
import SectionHeader from '../-components/section-layout/section-header'
import SectionLayout from '../-components/section-layout/section-layout'
import DashboardKpiCards from './-components/dashboard-kpi-cards'

type Counts = Record<CoverageSortingCriteria, number>

const DEFAULT_COUNTS = Object.fromEntries(
  Object.values(CoverageSortingCriteria).map((criteria) => [criteria, 5]),
) as Counts

export const Route = createFileRoute('/app/dashboard')({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const portfolioUserUuid = useAtomValue(portfolioFilterAtom)
  const [counts, setCounts] = useState<Counts>(DEFAULT_COUNTS)

  const kpiRequest: DashboardKpiRequest = { portfolioUserUuid }

  const coverageByCriteriaFilters: CoverageByCriteriaRequest = {
    counts,
    portfolioUserUuid,
  }

  return (
    <SectionLayout title={t('dashboard.title')}>
      <div className="flex w-full grow flex-col divide-y overflow-x-hidden">
        <SectionHeader title={t('dashboard.title')}>
          <PortfolioSelect className="h-10" />
        </SectionHeader>
        <div className="flex h-full flex-col gap-2 overflow-y-auto p-2">
          <DashboardKpiCards request={kpiRequest} />
          <InsistencesSection />
          <CoverageByCriteria
            counts={counts}
            setCounts={setCounts}
            filters={coverageByCriteriaFilters}
            rowLink={coverageRowLink}
            columnsByCriteria={COVERAGE_BY_CRITERIA_COLUMNS}
            incomingFirst
          />
          <PendingCoveragesSection />
          <NewPotentialCoveragesSection />
        </div>
      </div>
      <Outlet />
    </SectionLayout>
  )
}

const COVERAGE_BY_CRITERIA_COLUMNS: Partial<
  Record<CoverageSortingCriteria, CoveragesColumn[]>
> = {
  [CoverageSortingCriteria.IncomingReduction]: [
    CoveragesColumn.InsuredCompanyWithPolicyId,
    CoveragesColumn.DebtorCompanyWithId,
    CoveragesColumn.PermanentAmount,
    CoveragesColumn.TemporaryAmount,
    CoveragesColumn.NextReductionDate,
  ],
  [CoverageSortingCriteria.IncomingCancellation]: [
    CoveragesColumn.InsuredCompanyWithPolicyId,
    CoveragesColumn.DebtorCompanyWithId,
    CoveragesColumn.PermanentAmount,
    CoveragesColumn.NextReductionDate,
  ],
}

const DEFAULT_PAGINATION: CursorPaginationState = {
  pageSize: DEFAULT_PAGE_SIZE,
}

const DEFAULT_INSISTENCES_SORTING: InsistenceSortingState = [
  { id: 'date', desc: true },
]

const INSISTENCE_COLUMNS: InsistencesColumn[] = [
  InsistencesColumn.Date,
  InsistencesColumn.InsuredCompanyWithPolicyId,
  InsistencesColumn.Group,
  InsistencesColumn.DebtorCompanyWithId,
  InsistencesColumn.Country,
  InsistencesColumn.RequestedAmount,
]

const InsistencesSection = () => {
  const { t } = useTranslation()
  const portfolioUserUuid = useAtomValue(portfolioFilterAtom)

  const setInsistencesFilters = (
    updater: (filter?: InsistenceFilters) => InsistenceFilters,
  ) => updater({ ...insistenceFilters, status: InsistenceStatus.Pending })

  const [insistencesPagination, setInsistencesPagination] =
    useState<CursorPaginationState>(DEFAULT_PAGINATION)
  const [insistencesSorting, setInsistencesSorting] =
    useState<InsistenceSortingState>(DEFAULT_INSISTENCES_SORTING)

  const insistenceFilters: InsistenceFilters = {
    status: InsistenceStatus.Pending,
    portfolioUserUuid,
  }

  return (
    <Section title={t('dashboard.insistences.title')}>
      <InsistencesTable
        filters={insistenceFilters}
        setFilters={setInsistencesFilters}
        pagination={insistencesPagination}
        setPagination={setInsistencesPagination}
        sorting={insistencesSorting}
        setSorting={setInsistencesSorting}
        columnList={INSISTENCE_COLUMNS}
      />
    </Section>
  )
}

const DEFAULT_COVERAGES_SORTING: CoverageSortingState = []

const PENDING_COVERAGES_COLUMNS = [
  CoveragesColumn.RequestDate,
  CoveragesColumn.InsuredCompanyWithPolicyId,
  CoveragesColumn.DebtorCompanyWithId,
  CoveragesColumn.Country,
  CoveragesColumn.RequestedAmount,
]

const PendingCoveragesSection = () => {
  const { t } = useTranslation()
  const portfolioUserUuid = useAtomValue(portfolioFilterAtom)

  const setCoveragesFilters = (
    updater: (filter?: CoverageFilters) => CoverageFilters,
  ) => ({ ...updater(coverageFilters), status: CoverageStatus.Pending })

  const [coveragesPagination, setCoveragesPagination] =
    useState<CursorPaginationState>(DEFAULT_PAGINATION)
  const [coveragesSorting, setCoveragesSorting] =
    useState<CoverageSortingState>(DEFAULT_COVERAGES_SORTING)

  const coverageFilters: CoverageFilters = {
    status: CoverageStatus.Pending,
    portfolioUserUuid,
  }

  return (
    <Section title={t('dashboard.coverages.title')}>
      <CoveragesTable
        filters={coverageFilters}
        setFilters={setCoveragesFilters}
        pagination={coveragesPagination}
        setPagination={setCoveragesPagination}
        sorting={coveragesSorting}
        setSorting={setCoveragesSorting}
        rowLink={coverageRowLink}
        columnsList={PENDING_COVERAGES_COLUMNS}
      />
    </Section>
  )
}

const NEW_POTENTIAL_COVERAGES_COLUMNS = [
  CoveragesColumn.InsuredCompanyWithPolicyId,
  CoveragesColumn.DebtorCompanyWithId,
  CoveragesColumn.RequestedAmount,
  CoveragesColumn.AcceptedAmount,
  CoveragesColumn.DebtorLastRating,
  CoveragesColumn.DebtorNewRating,
]

const NewPotentialCoveragesSection = () => {
  const { t } = useTranslation()
  const portfolioUserUuid = useAtomValue(portfolioFilterAtom)

  const setCoveragesFilters = (
    updater: (filter?: CoverageFilters) => CoverageFilters,
  ) => ({
    ...updater(coverageFilters),
    newPotential: true,
    includeDebtorRatings: true,
  })

  const [coveragesPagination, setCoveragesPagination] =
    useState<CursorPaginationState>(DEFAULT_PAGINATION)
  const [coveragesSorting, setCoveragesSorting] =
    useState<CoverageSortingState>(DEFAULT_COVERAGES_SORTING)

  const coverageFilters: CoverageFilters = {
    newPotential: true,
    includeDebtorRatings: true,
    portfolioUserUuid,
  }

  return (
    <Section title={t('dashboard.potential-coverage.title')}>
      <CoveragesTable
        filters={coverageFilters}
        setFilters={setCoveragesFilters}
        pagination={coveragesPagination}
        setPagination={setCoveragesPagination}
        sorting={coveragesSorting}
        setSorting={setCoveragesSorting}
        rowLink={coverageRowLink}
        columnsList={NEW_POTENTIAL_COVERAGES_COLUMNS}
      />
    </Section>
  )
}

interface SectionProps {
  title: string
  children: React.ReactNode
}

const Section = ({ children, title }: SectionProps) => (
  <div className="flex flex-col gap-2 2xl:flex-row">
    <div className="flex h-120 max-h-120 min-h-120 flex-1 flex-col gap-6 overflow-hidden rounded-sm border p-4">
      <span className="text-text-secondary-700 text-sm font-bold">{title}</span>
      {children}
    </div>
  </div>
)

const coverageRowLink: TableMeta<CoverageResponse>['rowLink'] = (row) =>
  linkOptions({
    to: '/app/dashboard/$coverageUuid',
    params: { coverageUuid: row.uuid },
  })
